import { axiosBaseQuery } from '@client';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { EvaluationSummaryResponse } from '@state/types/EvaluationSummaryResponse';
import { BankDetailsResponse } from '@state/types/BankDetailsResponse';
import { EvaluationExpensesResponse } from '@state/types/EvaluationExpensesResponse';
import { ExpenseEvaluationResponse } from '@module/expense-evaluation/model/ExpenseEvaluationResponse';
import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { SplitEvaluationDTO } from '@module/evaluation/model/SplitEvaluationDTO';
import { ExpenseEvaluationVerifyForm } from '@state/types/ExpenseEvaluationVerifyForm';
import { PsUtilities } from 'components/components';
import { ExpenseEvaluationParametersForm } from '@module/expense-evaluation/model/ExpenseEvaluationParametersForm';
import { ExpenseEvaluationInspectionCalculationResponse } from '@module/expense-evaluation/model/ExpenseEvaluationInspectionCalculationResponse';
// TODO:  when backend is ready
// import { EvaluationAttachmentsResponse } from '@state/types/EvaluationAttachmentsResponse';

const expenseEvaluationEntityAdapter =
  createEntityAdapter<ExpenseEvaluationResponse>({
    selectId: ({ id }) => id
  });

export const { selectById: selectExpenseEvaluationById } =
  expenseEvaluationEntityAdapter.getSelectors();

/**
 * Extracts all expense evaluations from a section, including nested edit and split expenses
 */
const extractAllExpenseEvaluations = (
  expenseEvaluations: ExpenseEvaluationResponse[] | undefined
): ExpenseEvaluationResponse[] => {
  if (!expenseEvaluations) {
    return [];
  }

  const allExpenses: ExpenseEvaluationResponse[] = [];

  for (const expense of expenseEvaluations) {
    allExpenses.push(expense);

    if (expense.editExpenses?.length) {
      allExpenses.push(...extractAllExpenseEvaluations(expense.editExpenses));
    }

    if (expense.splitExpenses?.length) {
      allExpenses.push(...extractAllExpenseEvaluations(expense.splitExpenses));
    }
  }

  return allExpenses;
};

export const evaluationV2Api = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: 'secured/v2/evaluation' }),
  reducerPath: 'evaluation-v2-api',
  tagTypes: ['EVALUATION_V2'],
  endpoints: (build) => ({
    fetchEvaluationSummary: build.query<
      EvaluationSummaryResponse,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/summary`,
        method: 'GET'
      }),
      providesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: evaluationId }
      ]
    }),

    updateEvaluationInvoice: build.mutation<
      void,
      { evaluationId: number; invoiceNumber: string; invoiceDate: string }
    >({
      query: ({ evaluationId, invoiceNumber, invoiceDate }) => ({
        url: `/${evaluationId}/invoice`,
        method: 'POST',
        data: { invoiceNumber, invoiceDate }
      }),
      invalidatesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: evaluationId }
      ]
    }),

    updateEvaluationCurrency: build.mutation<
      void,
      { evaluationId: number; currency: string; rate: number }
    >({
      query: ({ evaluationId, currency, rate }) => ({
        url: `/${evaluationId}/currency-rate`,
        method: 'POST',
        data: { currency, rate }
      }),
      invalidatesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: evaluationId }
      ]
    }),

    fetchBankDetails: build.query<
      BankDetailsResponse,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/bank-account`,
        method: 'GET'
      }),
      providesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: `bank-details-${evaluationId}` }
      ]
    }),

    fetchEvaluationExpenses: build.query<
      EvaluationExpensesResponse & {
        evaluationExpensesEntities: EntityState<ExpenseEvaluationResponse>;
      },
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/expenses`,
        method: 'GET'
      }),
      transformResponse: (
        res: EvaluationExpensesResponse
      ): EvaluationExpensesResponse & {
        evaluationExpensesEntities: EntityState<ExpenseEvaluationResponse>;
      } => {
        // Collect all expense sections
        const expenseSections = [
          res.portExpenses,
          res.agencyExpenses,
          res.husbandryExpenses
        ];

        // Extract all expense evaluations from all sections, including nested ones
        const expenseEvaluations = expenseSections.flatMap((section) =>
          extractAllExpenseEvaluations(section?.expenseEvaluations)
        );

        const expenseEvaluationEntityState =
          expenseEvaluationEntityAdapter.setAll(
            expenseEvaluationEntityAdapter.getInitialState(),
            expenseEvaluations
          );

        return {
          ...res,
          evaluationExpensesEntities: expenseEvaluationEntityState
        };
      },
      providesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: `expenses-${evaluationId}` }
      ]
    }),

    verifyExpenseEvaluationV2: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: number;
        formValues: ExpenseEvaluationVerifyForm;
        attachments?: {
          principalsAccount: File[];
          ownersAccount: File[];
          charterersAccount: File[];
        };
      }
    >({
      query: ({ evaluationId, formValues, attachments }) => {
        const formData = PsUtilities.createFormRequestParts(
          formValues,
          attachments?.principalsAccount ?? []
        );

        if (attachments) {
          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.ownersAccount,
            'ownersAccountAttachments'
          );

          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.charterersAccount,
            'charterersAccountAttachments'
          );
        }
        return {
          url: `/${evaluationId}/expense/${formValues.id}/verify`,
          method: 'POST',
          formData: true,
          headers: {
            'Content-Type': 'multipart/form-data;'
          },
          data: formData
        };
      },
      invalidatesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: `expenses-${evaluationId}` }
      ]
    }),

    splitEvaluation: build.mutation<
      EvaluationResponse,
      { evaluationId: number; data: SplitEvaluationDTO }
    >({
      query: ({ evaluationId, data }) => ({
        url: `/${evaluationId}/split`,
        method: 'POST',
        data
      }),
      invalidatesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: evaluationId }
      ]
    }),

    updateEvaluationLocation: build.mutation<
      void,
      { evaluationId: number; terminalId: number; berthId: number }
    >({
      query: ({ evaluationId, terminalId, berthId }) => ({
        url: `/${evaluationId}/location`,
        method: 'POST',
        data: { terminalId, berthId }
      }),
      invalidatesTags: (_res, _err, { evaluationId }) => [
        { type: 'EVALUATION_V2', id: evaluationId }
      ]
    }),

    saveExpenseEvaluationParameters: build.mutation<
      ExpenseEvaluationInspectionCalculationResponse,
      { evaluationId: number; form: ExpenseEvaluationParametersForm }
    >({
      query: ({ evaluationId, form }) => ({
        url: `/${evaluationId}/expense/save-expense-evaluation-parameters`,
        method: 'POST',
        data: form
      }),
      invalidatesTags: (_res, _err, { evaluationId, form }) => [
        { type: 'EVALUATION_V2', id: `expenses-${evaluationId}` },
        // evalution v2 + evaluationId is the tag used by fetchEvaluationSummary
        { type: 'EVALUATION_V2', id: evaluationId },
        {
          type: 'EVALUATION_V2',
          id: `expense-params-${evaluationId}-${form.id}`
        }
      ]
    })

    // TODO: when backend is ready
    // fetchEvaluationAttachments: build.query<
    //   EvaluationAttachmentsResponse,
    //   { evaluationId: number }
    // >({
    //   query: ({ evaluationId }) => ({
    //     url: `/${evaluationId}/attachments`,
    //     method: 'GET'
    //   }),
    //   providesTags: (_res, _err, { evaluationId }) => [
    //     { type: 'EVALUATION_V2', id: `attachments-${evaluationId}` }
    //   ]
    // })
  })
});

export const {
  useFetchEvaluationSummaryQuery,
  useFetchBankDetailsQuery,
  useFetchEvaluationExpensesQuery,
  useUpdateEvaluationInvoiceMutation,
  useUpdateEvaluationCurrencyMutation,
  useSplitEvaluationMutation,
  useUpdateEvaluationLocationMutation,
  useVerifyExpenseEvaluationV2Mutation,
  useSaveExpenseEvaluationParametersMutation
  // TODO: when backend is ready
  // useFetchEvaluationAttachmentsQuery
} = evaluationV2Api;

export type StateWithEvaluationV2Api = {
  [evaluationV2Api.reducerPath]: ReturnType<typeof evaluationV2Api.reducer>;
};

export const invalidateEvaluationV2 = (evaluationId: number) =>
  evaluationV2Api.util.invalidateTags([
    { type: 'EVALUATION_V2', id: evaluationId }
  ]);
